<template>
    <div :class="['kc-date-picker', {active, 'input-group': isDateRange || clearable, 'is-invalid': state === false}]">
        <b-form-datepicker
            :id="id"
            ref="startDatePicker"
            v-model="startSelection"
            :class="['start', 'flex-nowrap', {'selected': !!startSelection}]"
            :data-cy="`${id}Start`"
            :disabled="disabled"
            :placeholder="placeholder"
            :readonly="readonly"
            :state="state === false ? false : null"
            v-bind="startDatePickerOptions"
            @hidden="active = false"
            @input="handleStartChange"
            @shown="active = true"
        >
            <template
                slot="button-content"
            >
                <slot
                    name="icon"
                >
                    <FontAwesomeIcon
                        :icon="['far', 'calendar']"
                    />
                </slot>
            </template>
        </b-form-datepicker>

        <template
            v-if="isDateRange"
        >
            <div
                class="separator"
                data-cy="datePickerRangeSeparator"
            >
                <slot name="separator">
                    <!-- `rangeSeparator` prop value -->
                    {{ rangeSeparator }}
                </slot>
            </div>

            <b-form-datepicker
                :id="`${id}End`"
                ref="endDatePicker"
                v-model="endSelection"
                class="end flex-nowrap w-auto"
                :data-cy="`${id}End`"
                :disabled="disabled"
                :placeholder="endPlaceholder"
                :readonly="readonly"
                :state="state === false ? false : null"
                v-bind="endDatePickerOptions"
                @hidden="active = false"
                @input="handleEndChange"
                @shown="active = true"
            >
                <template
                    slot="button-content"
                >
                    <slot
                        name="end-icon"
                    >
                        <!-- `<FontAwesomeIcon :icon="['far', 'calendar']" />` -->
                        <FontAwesomeIcon
                            :icon="['far', 'calendar']"
                        />
                    </slot>
                </template>
            </b-form-datepicker>
        </template>

        <b-input-group-append v-if="clearable">
            <b-button
                class="btn-clear"
                data-cy="clearDatePickerButton"
                @click="clear"
            >
                <slot name="clear-button-content">
                    <!-- `<FontAwesomeIcon :icon="['far', 'times']" class="m-0" />` -->
                    <FontAwesomeIcon
                        :icon="['far', 'times']"
                        class="m-0"
                    />
                </slot>
            </b-button>
        </b-input-group-append>
    </div>
</template>

<script>
    import {library} from '@fortawesome/fontawesome-svg-core';
    import {faCalendar, faTimes} from '@fortawesome/pro-regular-svg-icons';
    import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome';

    library.add(faCalendar, faTimes);

    /**
     * Date picker input. Now based on Bootstrap-Vue's `b-form-datepicker` component.
     */
    export default {
        name: 'DatePicker',
        components: {
            FontAwesomeIcon,
        },
        model: {
            prop: 'value',
            event: 'change',
        },
        props: {
            /**
             * Sets the input id on the date pickers. `End` is appended for ending date picker in `daterange` usage.
             */
            id: {
                type: String,
                required: true,
            },
            /**
             * Binding value for `v-model` usage.
             */
            value: {
                type: [Array, String],
                required: false,
                default: '',
            },
            /**
             * Whether the date picker inputs are read only or not.
             */
            readonly: {
                type: Boolean,
                required: false,
                // `false`
                default: false,
            },
            /**
             * Whether the date picker inputs are disabled or not.
             */
            disabled: {
                type: Boolean,
                required: false,
                // `false`
                default: false,
            },
            /**
             * Whether to show clear button or not.
             */
            clearable: {
                type: Boolean,
                required: false,
                // `true`
                default: true,
            },
            /**
             * The placeholder for the date picker and the start date picker in range mode.
             */
            placeholder: {
                type: String,
                required: false,
                // `MM/DD/YYYY`
                default: 'MM/DD/YYYY',
            },
            /**
             * The placeholder for the end date picker in range mode.
             */
            endPlaceholder: {
                type: String,
                required: false,
                // `MM/DD/YYYY`
                default: 'MM/DD/YYYY',
            },
            /**
             * Whether to show a valid or invalid state.
             */
            state: {
                type: Boolean,
                required: false,
                default: null,
            },
            /**
             * The type of date picker to use.
             */
            type: {
                // `date` / `daterange`
                type: String,
                required: false,
                // `date`
                default: 'date',
            },
            /**
             * The range separator text.
             */
            rangeSeparator: {
                type: String,
                required: false,
                // `To`
                default: 'To',
            },
            /**
             * Options passed on to the b-form-datepicker instances (both the start and end instances). Any provided
             * options are merged with the Kit Car defaults, with the provided overriding any defaults. End picker does
             * not allow overriding of `initialDate` or `dateDisabledFn` as those are based off the start picker value.
             */
            bFormDatePickerOptions: {
                type: Object,
                required: false,
                // `{
                //       dateFormatOptions: {
                //           month: '2-digit',
                //           day: '2-digit',
                //           year: 'numeric',
                //       },
                //       hideHeader: true,
                // }`
                default() {
                    return {};
                },
            },
        },
        data() {
            return {
                active: false,
            };
        },
        computed: {
            startSelection: {
                get() {
                    return this.type.includes('range') ? (this.value?.length ? this.value[0] : '') : this.value;
                },
                set(value) {
                    this.$emit('change', value);
                }
            },
            endSelection: {
                get() {
                    return (this.isDateRange && this.value?.length > 1) ? this.value[1] : '';
                },
                set(value) {
                    this.$emit('change', value);
                }
            },
            isDateRange() {
                return this.type.includes('range');
            },
            combinedDatePickerOptions() {
                return {
                    ...{
                        dateFormatOptions: {
                            month: '2-digit',
                            day: '2-digit',
                            year: 'numeric',
                        },
                        hideHeader: true,
                    },
                    ...this.bFormDatePickerOptions,
                };
            },
            endDatePickerOptions() {
                return {
                    ...this.combinedDatePickerOptions,
                    initialDate: this.startSelection,
                    dateDisabledFn: (date) => (this.startSelection && date < this.startSelection) ? true : false
                };
            },
            startDatePickerOptions() {
                return {
                    ...this.combinedDatePickerOptions,
                    dateDisabledFn: (date) => (this.endSelection && date > this.endSelection) ? true : false
                };
            },
        },
        methods: {
            clear() {
                this.startSelection = '';
                this.endSelection = '';
                this.$emit('change', this.isDateRange ? [] : '');
            },
            handleStartChange($event) {
                let value = $event;

                if (this.isDateRange) {
                    value = [$event, this.endSelection];
                }

                this.$emit('change', value);
                if (this.isDateRange) {
                    this.$refs.endDatePicker.$refs.control.show();
                }
            },
            handleEndChange($event) {
                this.$emit('change', [this.startSelection, $event]);
            },
        },
    };
</script>
